// src/components/CodeBlock.tsx
import React, { memo, useState, useCallback } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import MermaidDiagram from './MermaidDiagram';
import ChartRenderer from './ChartRenderer';
import SvgPreview from './SvgPreview';
import GenerateFileCard from './GenerateFileCard';

interface Props {
  language: string;
  code: string;
  filename?: string;
  onPreview: (code: string, language: string, filename?: string) => void;
}

// html/xml still go through the generic Preview-button path since
// PreviewPanel's combined-doc builder already knows how to run those.
// mermaid / chart-json / svg / pptx-json / pdf-json each have their own
// dedicated inline branch below instead.
const RUNNABLE = ['html', 'xml'];

function CopyIcon({ done }: { done: boolean }) {
  if (done) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="9" y="9" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M5 15H4a1 1 0 01-1-1V4a1 1 0 011-1h10a1 1 0 011 1v1"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CodeBlockInner({ language, code, filename, onPreview }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [code]);

  const lang = (language || '').toLowerCase();

  // ─── Phase 2 + 4: diagrams, charts, SVG, and generated-file cards ────────
  // Each of these returns early, before any of the existing
  // syntax-highlighted / Preview-button logic below, so nothing about the
  // existing code-file rendering path is touched for any other language.
  if (lang === 'mermaid') {
    return (
      <div className="code-block special-block">
        <div className="code-block-header">
          <span className="code-block-lang">{filename || 'Diagram'}</span>
          <div className="code-block-actions">
            <button className="code-block-btn" onClick={handleCopy}>
              <CopyIcon done={copied} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <MermaidDiagram code={code} />
      </div>
    );
  }

  if (lang === 'chart-json') {
    return (
      <div className="code-block special-block">
        <div className="code-block-header">
          <span className="code-block-lang">{filename || 'Chart'}</span>
          <div className="code-block-actions">
            <button className="code-block-btn" onClick={handleCopy}>
              <CopyIcon done={copied} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <ChartRenderer code={code} />
      </div>
    );
  }

  if (lang === 'svg') {
    return (
      <div className="code-block special-block">
        <div className="code-block-header">
          <span className="code-block-lang">{filename || 'SVG design'}</span>
          <div className="code-block-actions">
            <button className="code-block-btn" onClick={() => onPreview(code, language, filename)}>
              Expand
            </button>
            <button className="code-block-btn" onClick={handleCopy}>
              <CopyIcon done={copied} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <SvgPreview code={code} />
      </div>
    );
  }

  if (lang === 'pptx-json' || lang === 'pdf-json' || lang === 'docx-json' || lang === 'xlsx-json') {
    // No header chrome here (no Copy/Expand) — this isn't a code block the
    // user would want to copy as text, it's a spec that becomes a real
    // downloadable file. GenerateFileCard is self-contained.
    const fileType =
      lang === 'pptx-json' ? 'pptx' :
      lang === 'docx-json' ? 'docx' :
      lang === 'xlsx-json' ? 'xlsx' : 'pdf';
    return <GenerateFileCard type={fileType} code={code} />;
  }

  // ─── Everything below this line is unchanged from before Phase 2 ────────
  const isRunnable = RUNNABLE.includes(lang);
  const lineCount = code.split('\n').length;

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-block-lang">
          {filename ? filename : (language || 'text')}
          <span className="code-block-lines">{lineCount} lines</span>
        </span>
        <div className="code-block-actions">
          <button className="code-block-btn" onClick={() => onPreview(code, language, filename)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              {isRunnable ? (
                <>
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                    stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                </>
              ) : (
                <>
                  <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                </>
              )}
            </svg>
            {isRunnable ? 'Preview' : 'Expand'}
          </button>
          <button className="code-block-btn" onClick={handleCopy}>
            <CopyIcon done={copied} />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        style={oneDark as any}
        language={language || 'text'}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: '0 0 10px 10px', fontSize: 13.5 }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

// Memoized: only re-renders if the code content or language actually changes,
// not every time the parent message list re-renders.
export default memo(CodeBlockInner, (prev, next) =>
  prev.code === next.code && prev.language === next.language && prev.filename === next.filename
);